import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "@/contexts/auth-context";
import { fetchWithAuth } from "@/lib/api";
import { API_BASE } from "@/config/api";

interface BaseCurrency {
  symbol: string;
  code: string;
  loading: boolean;
}

interface BaseCurrencyContextType {
  currency: BaseCurrency;
}

const BaseCurrencyContext = createContext<BaseCurrencyContextType | undefined>(
  undefined
);

export function BaseCurrencyProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useAuth();
  const [currency, setCurrency] = useState<BaseCurrency>({
    symbol: "$",
    code: "USD",
    loading: true,
  });

  useEffect(() => {
    const fetchCurrency = async () => {
      if (!user?.base_currency_id) {
        setCurrency({ symbol: "$", code: "USD", loading: false });
        return;
      }

      try {
        const response = await fetchWithAuth(
          `${API_BASE}/assets/${user.base_currency_id}`
        );

        if (response.ok) {
          const data = await response.json();
          setCurrency({
            symbol: data.symbol || "$",
            code: data.symbol || "USD",
            loading: false,
          });
        } else {
          console.warn("Failed to fetch base currency details");
          setCurrency({ symbol: "$", code: "USD", loading: false });
        }
      } catch (error) {
        console.error("Error fetching base currency:", error);
        setCurrency({ symbol: "$", code: "USD", loading: false });
      }
    };

    fetchCurrency();
  }, [user?.base_currency_id]);

  return (
    <BaseCurrencyContext.Provider value={{ currency }}>
      {children}
    </BaseCurrencyContext.Provider>
  );
}

export const useBaseCurrency = (): BaseCurrency => {
  const context = useContext(BaseCurrencyContext);
  if (context === undefined) {
    throw new Error("useBaseCurrency must be used within a BaseCurrencyProvider");
  }
  return context.currency;
};
